import { sortBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';

import { useUserAllAppointments } from '@/hooks/useUserAllAppointments.js';

import { formatAppointments, hydrateAppointments, splitAppointmentsIntoTabs } from '@/utils/appointmentUtils.js';

import { useConfigStore, usePersistedPrismicStore } from '@/store.js';

/**
 * @typedef {import('@/models/appointment.ts').Appointment} PatientAppointment
 */

/**
 * @returns {{ upcoming: PatientAppointment[], past: PatientAppointment[], isLoading: boolean }}
 */
export const usePatientAppointments = () => {
    const { i18n } = useTranslation();
    const { data, isLoading } = useUserAllAppointments();
    const doctorData = usePersistedPrismicStore((state) => state.doctorData?.[i18n.language]);
    const locationData = usePersistedPrismicStore(
        (state) => state.locationData?.[i18n.language]?.allAppointmentsPrismicData
    );
    const currentCustomer = useConfigStore((state) => state.currentCustomer);

    if (!data || !doctorData) return { upcoming: [], past: [], isLoading: true };

    const active = hydrateAppointments(data.active ?? [], doctorData, locationData, i18n.language);
    const inactive = hydrateAppointments(data.inactive ?? [], doctorData, locationData, i18n.language);
    const { upcoming, past } = splitAppointmentsIntoTabs(active, inactive);

    const customerId = currentCustomer?.customer_id?.[0]?.text;
    const forCustomer = (appointments) =>
        customerId ? appointments.filter((appointment) => appointment.customer_id === customerId) : appointments;

    return {
        upcoming: formatAppointments(forCustomer(upcoming)),
        past: sortBy(forCustomer(past), [(appointment) => -new Date(appointment.start).getTime()]),
        isLoading: isLoading,
    };
};
